import clsx from "clsx";
import { FC, HTMLAttributes, ReactNode } from "react";
import Image from "next/image";
import Link from "next/link";

import ActionButton from "@/components/atoms/buttons/ActionButton";
import { ITextSectionContent } from "../common/TextSection";

// ----------------------------------------------------------------------------
// Customer trust + ratings section.
//
// Two-column layout on lg+:
//   - LEFT (col-span-5): canonical eyebrow + heading + description + CTA
//   - RIGHT (col-span-7): big rating image on top, certificate badges row
//     below
//
// On mobile/tablet stacks vertically. Uses the canonical clamp typography
// + orange CTA + light font weight so it matches the rest of the home
// page sections we've already redesigned.
// ----------------------------------------------------------------------------

type IRatingSectionProps = HTMLAttributes<HTMLDivElement> &
  ITextSectionContent & {
    certificateImages?: { image: string; href: string }[];
    ratingImage?: string;
    variant?: "primary" | "secondary" | "white";
  };

const bgColor = {
  primary: "bg-primary-50",
  secondary: "bg-secondary-50",
  white: "bg-white",
};

const RatingSection: FC<IRatingSectionProps> = ({
  heading,
  description,
  buttons,
  certificateImages,
  ratingImage,
  variant = "white",
  className,
  ...other
}) => {
  return (
    <section
      className={clsx(
        bgColor[variant],
        "py-10 md:py-14 lg:py-16 xl:py-20",
        className,
      )}
      {...other}
    >
      <div className="container">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-14">
          {/* LEFT — text column. Eyebrow + heading + description + CTA. */}
          <div className="text-center lg:col-span-5 lg:text-left">
            <p className="mb-3 font-bricolage-grotesque text-[clamp(0.75rem,0.4vw+0.65rem,0.875rem)] font-semibold uppercase tracking-[0.25em] text-primary-900">
              Top-rated on Clutch
            </p>
            {heading && (
              <h2 className="font-bricolage-grotesque text-[clamp(1.375rem,2.5vw+0.75rem,2.5rem)] font-extralight !leading-[1.15] tracking-tight text-black-950">
                {heading}
              </h2>
            )}
            {description && (
              <div className="mt-4 text-[clamp(0.875rem,0.5vw+0.75rem,1.125rem)] font-extralight leading-relaxed text-black-700">
                {description as ReactNode}
              </div>
            )}
            {buttons && buttons.length > 0 && (
              <div className="mt-6 flex flex-wrap items-center justify-center gap-3 lg:justify-start">
                {buttons.map((b, idx) => (
                  <ActionButton
                    key={b.id ?? idx}
                    id={b.id}
                    href={b.link}
                    target={b.target}
                    variant="primary"
                    size="md"
                    iconType={b.target === "_blank" ? "external" : "arrow"}
                  >
                    {b.text}
                  </ActionButton>
                ))}
              </div>
            )}
          </div>

          {/* RIGHT — visual column. Big rating image on top, certificate
              badges row below. The whole panel sits in a soft card so it
              reads as a "trust artifact" rather than scattered images. */}
          {(ratingImage || certificateImages) && (
            <div className="lg:col-span-7">
              <div
                className={clsx(
                  "rounded-2xl border p-6 md:p-8 lg:p-10",
                  // Card background contrasts with the section bg:
                  //   - white section → light primary tint card
                  //   - tinted section → white card
                  variant === "white"
                    ? "border-primary-100 bg-primary-50/50"
                    : "border-black-100 bg-white",
                )}
              >
                {ratingImage && (
                  <div className="mb-6 flex flex-col items-center gap-2 md:mb-7">
                    <Image
                      className="h-10 w-auto md:h-12 lg:h-14"
                      height={56}
                      width={320}
                      src={ratingImage}
                      alt="Customer rating"
                      quality={100}
                      unoptimized={true}
                    />
                    <p className="text-xs font-normal uppercase tracking-wider text-black-500">
                      Average across all verified reviews
                    </p>
                  </div>
                )}

                {certificateImages && certificateImages.length > 0 && (
                  <>
                    <div className="mb-4 h-px w-full bg-black-100" />
                    <p className="mb-4 text-center text-xs font-medium uppercase tracking-wider text-black-500">
                      Top-rated by
                    </p>
                    <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-5 md:gap-x-10 lg:gap-x-12">
                      {certificateImages.map(({ image, href }) => (
                        <Link
                          href={href}
                          target="_blank"
                          key={image}
                          className="transition-transform duration-300 hover:-translate-y-0.5"
                        >
                          <Image
                            height={120}
                            width={85}
                            src={image}
                            className="h-20 w-auto md:h-24 lg:h-[120px]"
                            alt="Certification badge"
                            quality={100}
                            unoptimized={true}
                          />
                        </Link>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default RatingSection;
